import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../../hooks/useAuthFixed';
import {
  ArrowRightIcon,
  MenuProfileIcon,
  MenuFamilyIcon,
  MenuTermsIcon,
  MenuPrivacyIcon,
} from '../../../components/Icons';
import { PageContainer, ContentCard, PageHeading, TextButton } from '../../../components';

interface MenuProps {
  onNavigateToProfile: () => void;
  onNavigateToFamilyList: () => void;
  onBack: () => void;
}

// Styled Components

const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.h2`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 2px 0;
`;

const UserNameHonorific = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
`;

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  width: 100%;
  min-height: 80px;
  box-sizing: border-box;
  text-align: left;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }
`;

const MenuIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
`;

const MenuContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const MenuTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 2px 0;
`;

const MenuDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
`;

const MenuArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

const Footer = styled.div`
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const AppVersion = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin: 0 0 4px 0;
`;

const Copyright = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin: 0;
  color: ${({ theme }) => theme.colors.text.muted};
`;

// reverse tabnabbing 対策: noopener/noreferrer 指定に加え、保険として opener も明示的に null 化する
const openExternal = (url: string) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.opener = null;
  }
};

export const Menu: React.FC<MenuProps> = ({
  onNavigateToProfile,
  onNavigateToFamilyList,
  onBack
}) => {
  const { profile } = useAuth();

  const menuItems = useMemo<
    Array<{
      id: string;
      title: string;
      description: string;
      icon: React.ReactNode;
      onClick: () => void;
    }>
  >(
    () => [
      {
        id: 'profile',
        title: 'プロフィール',
        description: '個人情報を確認・編集',
        icon: <MenuProfileIcon size={48} />,
        onClick: onNavigateToProfile,
      },
      {
        id: 'families',
        title: '家族一覧',
        description: '連携中の家族を確認',
        icon: <MenuFamilyIcon size={48} />,
        onClick: onNavigateToFamilyList,
      },
      {
        id: 'terms',
        title: '利用規約',
        description: '',
        icon: <MenuTermsIcon size={48} />,
        onClick: () => openExternal('https://cdn.rehab.cloud/familyapp/terms_of_service.pdf'),
      },
      {
        id: 'privacy',
        title: 'プライバシーポリシー',
        description: '',
        icon: <MenuPrivacyIcon size={48} />,
        onClick: () => openExternal('https://cdn.rehab.cloud/familyapp/privacy_policy.pdf'),
      },
    ],
    [onNavigateToProfile, onNavigateToFamilyList],
  );

  return (
    <PageContainer>
      <PageHeading>設定</PageHeading>

      <ContentCard>
        <MenuSection>
          {/* ユーザー情報 */}
          <UserCard>
            <UserInfo>
              <UserName>
                {profile?.displayName || 'ユーザー'}
                <UserNameHonorific> さま</UserNameHonorific>
              </UserName>
            </UserInfo>
          </UserCard>

          {/* メニューリスト */}
          <MenuList>
            {menuItems.map((item) => (
              <MenuItem
                key={item.id}
                onClick={item.onClick}
              >
                <MenuIcon>
                  {item.icon}
                </MenuIcon>
                <MenuContent>
                  <MenuTitle>
                    {item.title}
                  </MenuTitle>
                  <MenuDescription>
                    {item.description}
                  </MenuDescription>
                </MenuContent>
                <MenuArrow>
                  <ArrowRightIcon />
                </MenuArrow>
              </MenuItem>
            ))}
          </MenuList>
        </MenuSection>

        <TextButton onClick={onBack} aria-label="戻る">
          戻る
        </TextButton>
      </ContentCard>

      {/* フッター情報 */}
      <Footer>
        <AppVersion>
          Rehab スマホ連絡帳 v0.1.0
        </AppVersion>
        <Copyright>
          © 2025 Rehab for JAPAN Inc. All rights reserved.
        </Copyright>
      </Footer>
    </PageContainer>
  );
};

export default Menu;
